import Seo from "@/components/Seo";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Search, ExternalLink, Star, Users, Clock, Check, GitCompare, X } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Empty } from "@/components/Shared";
import ListToolModal from "@/components/ListToolModal";
import { useConfig } from "@/lib/useConfig";

const CATS = ["All", "Chat & Assistants", "Coding & Development", "Image Generation",
  "Video Generation", "Audio & Voice", "Writing & Content", "Productivity & Automation",
  "Analytics & Research", "AI Infrastructure", "AI Agents", "AI Hardware", "E-Commerce"];

const badgeColor = (badge) => {
  if (!badge) return "";
  if (badge === "Upcoming") return "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/30";
  if (/(New|Just Launched)/i.test(badge)) return "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30";
  return "bg-[#00F0FF]/15 text-[#00F0FF] border-[#00F0FF]/30";
};

export default function Tools() {
  const [tools, setTools] = useState([]);
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("search") || "");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [listOpen, setListOpen] = useState(false);
  const { paymentsEnabled } = useConfig();
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/tools", { params: { category: cat, search } })
      .then((r) => setTools(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);

  const toggleCompare = (t) => {
    setSelected((prev) => {
      if (prev.find((x) => x.id === t.id)) return prev.filter((x) => x.id !== t.id);
      if (prev.length >= 3) return prev;
      return [...prev, { id: t.id, name: t.name }];
    });
  };
  const isSelected = (id) => selected.some((x) => x.id === id);

  return (
    <div data-testid="tools-page">
      <Seo title="Best AI Tools 2026 — Compare Tools by Category | WorldAIInfo" description="Browse and compare the best AI tools across 13 categories — features, pricing and ratings. Find the right AI tool for your workflow, free." canonicalPath="/tools" />
      <ListToolModal open={listOpen} onClose={() => setListOpen(false)} />
      <PageHeader overline="Marketplace" title="AI Tools Marketplace"
        subtitle="Discover the tools powering the AI revolution. Featured listings appear first in each category." />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="mb-8 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="list-tool-banner">
          <p className="text-sm text-[#fbbf24]"><span className="font-semibold">Built an AI tool?</span> {paymentsEnabled ? "Get it in front of our growing community — listings from $29." : "Get it in front of our growing community — free while we're in pre-launch."}</p>
          <button onClick={() => setListOpen(true)} data-testid="list-with-us-btn"
            className="bg-[#fbbf24] text-[#1a1204] font-semibold px-5 py-2.5 rounded-md hover:brightness-110 transition whitespace-nowrap">{paymentsEnabled ? "List with us" : "List a Tool — Free"}</button>
        </div>
        <div className="flex flex-col gap-4 mb-8">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input data-testid="tools-search" value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search tools, companies or tags…"
              className="w-full bg-[#111827] border border-white/10 rounded-md pl-10 pr-4 py-2.5 text-sm focus:border-[#00F0FF] focus:outline-none" />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`tools-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                className={`font-mono-tech text-xs px-3 py-2 rounded border transition ${cat === c ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 text-[#94A3B8] hover:border-white/25"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {loading ? <Empty text="Loading…" /> : tools.length === 0 ? <Empty text="No tools found." /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((t, i) => {
              const coming = t.availability === "coming_soon";
              return (
              <motion.div key={t.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i, 12) * 0.03 }}
                className={`rounded-xl p-6 flex flex-col relative ${t.featured ? "card-surface featured-glow" : "card-surface"}`}
                data-testid={`tool-card-${t.id}`}>
                {t.badge && (
                  <span className={`absolute top-4 right-4 font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded-full border ${badgeColor(t.badge)}`} data-testid={`tool-badge-${t.id}`}>
                    {t.badge}
                  </span>
                )}
                <div className="w-11 h-11 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center font-display font-bold text-[#00F0FF] text-lg overflow-hidden mb-3">
                  {t.logo_url ? <img src={t.logo_url} alt={t.name} className="w-full h-full object-cover" /> : (t.name[0] || "?")}
                </div>
                <h3 className="font-display font-bold text-xl pr-16">
                  <Link to={`/tools/${t.id}`} className="hover:text-[#00F0FF] transition-colors" data-testid={`tool-name-${t.id}`}>{t.name}</Link>
                </h3>
                {t.company && <p className="text-xs text-[#94A3B8] mb-2">by {t.company}</p>}
                <p className="text-sm text-[#94A3B8] flex-1 line-clamp-3">{t.description}</p>

                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(t.tags || []).slice(0, 3).map((tag) => (
                    <span key={tag} className="font-mono-tech text-[10px] text-[#94A3B8] bg-white/5 border border-white/10 rounded px-2 py-0.5">{tag}</span>
                  ))}
                </div>

                <div className="flex items-center gap-4 mt-4 text-xs text-[#94A3B8] font-mono-tech">
                  {t.rating ? <span className="flex items-center gap-1 text-[#F59E0B]"><Star className="w-3.5 h-3.5 fill-[#F59E0B]" /> {t.rating}</span>
                    : coming ? <span className="flex items-center gap-1 text-[#F59E0B]"><Clock className="w-3.5 h-3.5" /> Coming soon</span> : null}
                  {t.users && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {t.users}</span>}
                </div>
                <p className="font-mono-tech text-[11px] text-[#94A3B8] mt-3 truncate">{t.pricing_model}</p>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10 gap-2">
                  <button onClick={() => toggleCompare(t)} data-testid={`tool-compare-toggle-${t.id}`}
                    className={`font-mono-tech text-[11px] flex items-center gap-1.5 px-2 py-1 rounded border transition shrink-0 ${isSelected(t.id) ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10" : "border-white/15 text-[#94A3B8] hover:border-[#00F0FF]/50 hover:text-[#00F0FF]"}`}>
                    {isSelected(t.id) ? <Check className="w-3.5 h-3.5" /> : <GitCompare className="w-3.5 h-3.5" />}
                    {isSelected(t.id) ? "Added" : "Compare"}
                  </button>
                  <a href={t.website} target="_blank" rel="noreferrer" data-testid={`tool-visit-${t.id}`}
                    className="text-sm text-[#00F0FF] flex items-center gap-1 hover:gap-2 transition-all shrink-0">
                    {coming ? "Learn more" : "Visit"} <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </motion.div>
            );})}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#111827] border border-[#00F0FF]/40 rounded-full shadow-2xl px-4 py-3 flex items-center gap-3 max-w-[92vw]" data-testid="compare-bar">
          <span className="font-mono-tech text-xs text-[#94A3B8] shrink-0">{selected.length}/3 selected:</span>
          <div className="flex items-center gap-2 overflow-x-auto">
            {selected.map((s) => (
              <span key={s.id} className="flex items-center gap-1 text-xs bg-white/5 border border-white/10 rounded-full pl-2.5 pr-1 py-1 whitespace-nowrap">
                {s.name}
                <button onClick={() => setSelected(selected.filter((x) => x.id !== s.id))} className="text-[#94A3B8] hover:text-[#EF4444]"><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
          </div>
          <button disabled={selected.length < 2} data-testid="compare-go"
            onClick={() => navigate(`/compare?ids=${selected.map((s) => s.id).join(",")}`)}
            className="shrink-0 bg-[#00F0FF] text-[#04121a] font-semibold text-sm px-4 py-2 rounded-full hover:brightness-110 transition disabled:opacity-40">
            Compare
          </button>
        </div>
      )}
    </div>
  );
}
